import React from 'react';
import { X, Pin, Check, RotateCcw } from 'lucide-react';

export type MenuTone = 'blue' | 'amber' | 'cyan' | 'emerald' | 'violet' | 'indigo';

export interface MenuEntry {
  id: string;
  label: string;
  /** Δεύτερη γραμμή κάτω από τον τίτλο (π.χ. έκδοση εγχειριδίου, σφάλμα λήψης) */
  hint?: string;
  hintTone?: 'muted' | 'error';
  icon: React.ElementType;
  tone: MenuTone;
  onSelect: () => void;
  /** Αριθμός πάνω στο εικονίδιο (π.χ. εκκρεμείς αλλαγές καταλόγου) */
  badge?: number;
  /** Κείμενο δεξιά (π.χ. «3 νέες», «Νέα έκδοση») */
  trailing?: string;
  trailingTone?: 'alert' | 'info';
  /** Εμφανίζεται στο μενού ΑΚΟΜΑ κι αν δεν είναι καρφιτσωμένη (κάτι περιμένει τον χρήστη) */
  alert?: boolean;
  /** Μένει ανοιχτό το μενού μετά το πάτημα (η ενέργεια το κλείνει μόνη της) */
  keepOpen?: boolean;
  busy?: boolean;
}

export const TONE_CLASSES: Record<MenuTone, { bg: string; icon: string }> = {
  blue: { bg: 'bg-blue-600/15', icon: 'text-blue-600 dark:text-blue-400' },
  amber: { bg: 'bg-amber-600/15', icon: 'text-amber-700 dark:text-amber-400' },
  cyan: { bg: 'bg-cyan-600/15', icon: 'text-cyan-600 dark:text-cyan-400' },
  emerald: { bg: 'bg-emerald-600/15', icon: 'text-emerald-600 dark:text-emerald-400' },
  violet: { bg: 'bg-violet-600/15', icon: 'text-violet-600 dark:text-violet-400' },
  indigo: { bg: 'bg-indigo-600/15', icon: 'text-indigo-600 dark:text-indigo-400' },
};

export const trailingClass = (entry: MenuEntry) => (entry.trailingTone === 'info'
  ? 'text-indigo-600 dark:text-indigo-400'
  : 'text-red-600 dark:text-red-400');

export const MenuEntryIcon: React.FC<{ entry: MenuEntry; size?: 'sm' | 'lg' }> = ({ entry, size = 'sm' }) => {
  const Icon = entry.icon;
  const tone = TONE_CLASSES[entry.tone];
  const box = size === 'lg' ? 'w-11 h-11' : 'w-9 h-9';
  return (
    <div className={`relative ${box} ${tone.bg} rounded-xl flex items-center justify-center shrink-0`}>
      <Icon className={`w-5 h-5 ${tone.icon} ${entry.busy ? 'animate-pulse' : ''}`} />
      {!!entry.badge && entry.badge > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold">
          {entry.badge > 99 ? '99+' : entry.badge}
        </span>
      )}
    </div>
  );
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  entries: MenuEntry[];
  pinned: Set<string>;
  onTogglePin: (id: string) => void;
  onResetPins: () => void;
  /** true όταν ο χρήστης έχει δική του επιλογή (δείχνει το «Επαναφορά») */
  customized: boolean;
  onSelect: (entry: MenuEntry) => void;
}

/**
 * «Περισσότερα» του μενού χρήστη: ΟΛΕΣ οι διαθέσιμες επιλογές σε μεγάλα
 * κουμπιά αφής. Κάθε επιλογή έχει διακόπτη «Στο μενού» — όσες είναι ενεργές
 * εμφανίζονται κατευθείαν στο μενού πάνω δεξιά.
 */
const MenuMoreModal: React.FC<Props> = ({ isOpen, onClose, entries, pinned, onTogglePin, onResetPins, customized, onSelect }) => {
  if (!isOpen) return null;

  return (
    <div
      className="cashier-overlay fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="cashier-dialog bg-surface-0 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Όλες οι επιλογές"
      >
        <div className="cashier-dialog-header flex items-center justify-between gap-3 px-5 py-4 border-b border-line bg-surface-1 shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-t1">Όλες οι επιλογές</h2>
            <p className="text-xs text-t3 mt-0.5">
              Με το «Στο μενού» διαλέγεις ποιες φαίνονται κατευθείαν όταν πατάς το όνομά σου.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl bg-surface-2 hover:bg-surface-3 flex items-center justify-center active:scale-95 transition-all shrink-0"
            title="Κλείσιμο"
          >
            <X className="h-5 w-5 text-t2" />
          </button>
        </div>

        <div className="overflow-y-auto p-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {entries.map(entry => {
            const isPinned = pinned.has(entry.id);
            return (
              <div
                key={entry.id}
                className={`flex items-stretch rounded-xl border transition-colors ${isPinned
                  ? 'border-blue-300 dark:border-blue-700/60 bg-blue-50/60 dark:bg-blue-950/20'
                  : 'border-line bg-surface-1'}`}
              >
                <button
                  onClick={() => onSelect(entry)}
                  disabled={entry.busy}
                  className="flex-1 min-w-0 flex items-center gap-3 px-3 py-3 text-left rounded-l-xl hover:bg-surface-3/60 active:bg-surface-3 transition-colors touch-manipulation disabled:opacity-70"
                >
                  <MenuEntryIcon entry={entry} size="lg" />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-t1 truncate">{entry.label}</span>
                    {entry.hint && (
                      <span className={`block text-xs truncate ${entry.hintTone === 'error' ? 'text-red-600 dark:text-red-400' : 'text-t3'}`}>
                        {entry.hint}
                      </span>
                    )}
                    {entry.trailing && (
                      <span className={`block text-xs font-semibold truncate ${trailingClass(entry)}`}>{entry.trailing}</span>
                    )}
                  </span>
                </button>
                <button
                  onClick={() => onTogglePin(entry.id)}
                  aria-pressed={isPinned}
                  title={isPinned ? 'Αφαίρεση από το μενού' : 'Προσθήκη στο μενού'}
                  className={`shrink-0 w-[92px] flex flex-col items-center justify-center gap-0.5 border-l rounded-r-xl text-[11px] font-semibold transition-colors touch-manipulation active:scale-95 ${isPinned
                    ? 'border-blue-300 dark:border-blue-700/60 text-blue-700 dark:text-blue-300 bg-blue-100/70 dark:bg-blue-900/30'
                    : 'border-line text-t3 hover:text-t2 hover:bg-surface-3/60'}`}
                >
                  {isPinned ? <Check className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                  {isPinned ? 'Στο μενού' : 'Βάλ’ το στο μενού'}
                </button>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-line bg-surface-1 shrink-0">
          {customized ? (
            <button
              onClick={onResetPins}
              className="flex items-center gap-1.5 text-sm text-t3 hover:text-t1 px-2 py-1.5 rounded-lg hover:bg-surface-3 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Επαναφορά προεπιλογών
            </button>
          ) : <span />}
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-surface-3 hover:bg-surface-2 text-sm font-semibold text-t1 transition-colors"
          >
            Κλείσιμο
          </button>
        </div>
      </div>
    </div>
  );
};

export default MenuMoreModal;
